import type { DateString, Nullable, UUID } from '../../lib/typeHelpers.no-deps'
import * as DBTypes from '../types.no-deps'
import type { PostEvent } from '../postEvents/types.no-deps'
import type { PlainTextComment, RichTextComment } from '../postComments/types.no-deps'
import type { ProcedureEventDto } from '../procedureEvents/types.no-deps'
import type { UnitEventDto } from '../unitEvents/types.no-deps'
import type { EncryptedText } from '../../lib/crypto-node'
import type { ActionEventDto } from '../actionEvents/types.no-deps'
import type { ElementOrNested } from '../flows/element.no-deps'
import type { WorkflowType } from '../workflows'

export type EventTypeFilterOptions = 'guides' | 'procedure-changes' | 'reports' | 'tasks' | 'training'

export type ModificationType = 'created' | 'updated' | 'deleted' | 'commented'
export const modificationTypes: ModificationType[] = ['created', 'updated', 'deleted', 'commented']

export type UnitTarget = {
    unit: number
    location?: Nullable<number>
    workflow?: Nullable<number>
    procedure?: Nullable<number>
}

export type LocationTarget = {
    unit?: null
    location: number
    workflow?: Nullable<number>
    procedure?: Nullable<number>
}

export type ProcedureTarget = {
    unit?: null
    location?: null
    workflow?: Nullable<number>
    procedure: number
}

// https://www.notion.so/zaptic/Procedures-Audit-trail-f7047ca862a140b1b64a00f6be3bb624#7f7882d96868460a9906ca9fc7ab42df
export type PostsSortBy = 'RecentEvents' | 'LatestActivity'

/**
 * Some documentation about targets:
 *
 * Targets are meant to represent what a post is about and related to.
 * At a database level, all 4 fields are in the same table (procedure, workflow, location, unit).
 * The field being null or absent bears a particular meaning. A good way to form intuition about it is to remember that
 * posts target the most specific entity that they are linked to and that the most specific is a unit and the least is
 * a procedure.
 *
 * When representing a post about a unit we will have a target pointing at a unit and the row/data will have a unit
 * attribute. Additionally it might be related to a location, workflow and/or procedure, in which case the row/data
 * will potentially have non empty values.
 * E.G. (unit: 5; location: null; procedure: 2; workflow: null)
 *
 * When representing a post about a location the unit must be null or absent from the data structure because otherwise
 * it would be a post about a unit. It may have a procedure and/or a workflow attached.
 * E.G. (unit: null, location: 2, procedure: 1, workflow: null)
 *
 * Finally when representing a post about a procedure the location and unit must be null
 * E.G. (unit: null, location: null, procedure: 2, workflow: 5)
 *
 * A post cannot be about a workflow alone.
 */
export type Target = UnitTarget | LocationTarget | ProcedureTarget

export function isUnitTarget(target: Target): target is UnitTarget {
    return (target as UnitTarget).unit != null
}

export function isLocationTarget(target: Target): target is LocationTarget {
    return !isUnitTarget(target) && (target as LocationTarget).location != null
}

export function isProcedureTarget(target: Target): target is ProcedureTarget {
    return !isUnitTarget(target) && !isLocationTarget(target)
}

export interface Base {
    id: UUID
    createdAt: Date
    createdBy: number
    lastModifiedAt: Date
    lastModifiedBy: number
    lastModifiedType: ModificationType
    isDeleted: boolean
    serverSyncTime: Nullable<Date> // Only null when being sent to the server, the server will always return this
}

export interface ContentBase {
    id: UUID
    post: Post['id']
    createdAt: Date
    createdBy: number
}

export type ContentType = DBTypes.PostContent.ContentType
export const ContentType = DBTypes.PostContent.ContentType
export const contentTypes: ContentType[] = DBTypes.PostContent.contentType

export interface ReportContent {
    contentType: DBTypes.PostContent.ContentType.report
    report: number
    reportActivities: number
    reportWorkflowName: string
    reportClientId?: UUID // This is optional for the app, it might not always be sending it
    reportParentClientId?: UUID
    reportAuthor?: number
    reportIsConfidential?: boolean
    reportTitle?: string
    encryptedReportTitle?: EncryptedText
    reportStatus?: number
    reportWorkflowId?: number
    reportActionsCreated?: number
    reportActionsCompleted?: number

    text?: null
    language?: null
    mimeType?: null
    url?: null
    title?: null
    fileSizeInBytes?: null
    thumbnail?: null
    processingStatus?: null
    procedureEvent?: null
    procedureEventData?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: WorkflowType
}

export interface FullReportContent extends ContentBase, ReportContent {}

export interface ReportReferenceContent {
    contentType: DBTypes.PostContent.ContentType.reportReference
    report: number

    reportClientId?: UUID
    reportParentClientId?: UUID
    reportTitle?: string
    encryptedReportTitle?: EncryptedText
    reportStatus?: number
    reportWorkflowId?: number
    reportAuthor?: number
    reportIsConfidential?: boolean

    text?: null
    language?: null
    mimeType?: null
    url?: null
    title?: null
    fileSizeInBytes?: null
    thumbnail?: null
    processingStatus?: null
    reportWorkflowName?: null
    reportActivities?: null
    procedureEvent?: null
    procedureEventData?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: WorkflowType
}

export interface FullReportReferenceContent extends ContentBase, ReportReferenceContent {}

export interface TextContent {
    contentType: DBTypes.PostContent.ContentType.text
    text: string
    language?: string

    mimeType?: null
    url?: null
    title?: null
    fileSizeInBytes?: null
    thumbnail?: null
    processingStatus?: null
    report?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    procedureEvent?: null
    procedureEventData?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: null
}

export interface FullTextContent extends ContentBase, TextContent {}

export interface FileContent {
    contentType: DBTypes.PostContent.ContentType.file
    mimeType: string
    url: string
    title: string
    fileSizeInBytes: number

    text?: null
    language?: null
    thumbnail?: null
    processingStatus?: null
    report?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    procedureEvent?: null
    procedureEventData?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: null
}

export interface FullFileContent extends ContentBase, FileContent {}

export interface PhotoContent {
    contentType: DBTypes.PostContent.ContentType.photo
    url: string
    title: string
    thumbnail: string
    fileSizeInBytes: number

    text?: null
    language?: null
    mimeType?: null
    processingStatus?: null
    report?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    procedureEvent?: null
    procedureEventData?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: null
}

export interface FullPhotoContent extends ContentBase, PhotoContent {}

export interface VideoContent {
    contentType: DBTypes.PostContent.ContentType.video
    url: string
    title: string
    thumbnail: string
    fileSizeInBytes: number
    processingStatus: 'processing' | 'error' | 'success'

    text?: null
    language?: null
    mimeType?: null
    report?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    procedureEvent?: null
    procedureEventData?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: null
}

export interface FullVideoContent extends ContentBase, VideoContent {}

export interface ProcedureEventContent {
    contentType: DBTypes.PostContent.ContentType.procedureEvent
    procedureEvent: number

    procedureEventData?: ProcedureEventDto

    text?: null
    language?: null
    mimeType?: null
    url?: null
    title?: null
    fileSizeInBytes?: null
    thumbnail?: null
    processingStatus?: null
    report?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    unitEvent?: null
    unitEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: null
}

export interface FullProcedureEventContent extends ContentBase, ProcedureEventContent {}

export interface UnitEventContent {
    contentType: DBTypes.PostContent.ContentType.unitEvent
    unitEvent: number
    unitEventData?: UnitEventDto

    text?: null
    language?: null
    mimeType?: null
    url?: null
    title?: null
    fileSizeInBytes?: null
    thumbnail?: null
    processingStatus?: null
    report?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    procedureEvent?: null
    procedureEventData?: null
    actionEvent?: null
    actionEventData?: null
    workflowType?: null
}

export interface FullUnitEventContent extends ContentBase, UnitEventContent {}

export interface ActionEventContent {
    contentType: DBTypes.PostContent.ContentType.actionEvent
    actionEvent: number
    actionEventData?: ActionEventDto

    text?: null
    language?: null
    mimeType?: null
    url?: null
    title?: null
    fileSizeInBytes?: null
    thumbnail?: null
    processingStatus?: null
    report?: null
    unitEvent?: null
    unitEventData?: null
    reportAuthor?: null
    reportIsConfidential?: null
    reportWorkflowName?: null
    reportActivities?: null
    reportClientId?: null
    reportParentClientId?: null
    reportTitle?: null
    reportStatus?: null
    reportWorkflowId?: null
    procedureEvent?: null
    procedureEventData?: null
    workflowType?: null
}

export interface FullActionEventContent extends ContentBase, ActionEventContent {}

export type Content =
    | TextContent
    | FileContent
    | PhotoContent
    | VideoContent
    | ReportContent
    | ReportReferenceContent
    | ProcedureEventContent
    | UnitEventContent
    | ActionEventContent

export type FullContent =
    | FullTextContent
    | FullFileContent
    | FullPhotoContent
    | FullVideoContent
    | FullReportContent
    | FullReportReferenceContent
    | FullProcedureEventContent
    | FullUnitEventContent
    | FullActionEventContent

export interface Ordering {
    timeline: UUID[]
    file: UUID[]
    photo: UUID[]
    text: UUID[]
    video: UUID[]
    unitEvent: UUID[]
    actionEvent: UUID[]
}

interface UnitPost extends Base, UnitTarget {}
interface ProcedurePost extends Base, ProcedureTarget {}
interface LocationPost extends Base, LocationTarget {}

export type Post = UnitPost | ProcedurePost | LocationPost

/**
 * Exists because the schema compiler can't compile Omit<A & (B | C)> properly
 */
export type PostWithContent = Post & Omit<ContentBase, 'post'> & Content

export type SyncPost = PostWithContent & { commentCount: number; reportCanBeInvalidated?: boolean }

export type SyncFilePost = Extract<SyncPost, { contentType: DBTypes.PostContent.FileContentType }>

export type ReportSyncPost = Omit<ReportContent, 'report'> & Post & Omit<ContentBase, 'post'> & { commentCount: number }

export namespace Endpoints {
    export type SyncInput = {
        posts: PostWithContent[]
        lastSyncTime: DateString
        teams: number[]
        includeNewPostTypes?: boolean
        unitIds?: number[]
        excludeFiles?: boolean
        includeLanguage?: boolean
        requireUnitEventPosts?: boolean
    }
    export type SyncOutput = {
        posts: SyncPost[]
        syncTime: DateString
        teams: number[]
        failures: Array<{ reason: string; id: UUID }>
        postIdsToDelete: string[]
    }

    export type GetInput = { posts: UUID[]; excludeFiles?: boolean }
    export type GetOutput = { posts: SyncPost[] }

    export type GetContentPageOrderInput = { target: Target } & {
        sortBy?: PostsSortBy
        filterByText?: string
        sinceDate?: Date
        tillDate?: Date
        filterByEventTypes?: EventTypeFilterOptions[]
        filterByCollaboratorUsers?: number[]
        filterByCollaboratorTeams?: number[]
        pageSize?: number
        startAfterId?: UUID
        excludeFiles?: boolean
    }
    export type GetContentPageOrderOutput = { ordering: Ordering }

    export type FileGuidePost = Target & (FileContent | PhotoContent | Omit<VideoContent, 'processingStatus'>)
}

export type FilePostWithContent = Base & Omit<ContentBase, 'post'> & (FileContent | PhotoContent | VideoContent)

// Post timeline
export type PostEventTimelineItem = { type: 'event'; event: PostEvent }
export type PostCommentTimelineItem<Comment> = {
    type: 'comment'
    comment: Comment
    quotedElement?: Pick<ElementOrNested, 'type' | 'label'>
}
export type PostTimelineItem<Comment> = PostEventTimelineItem | PostCommentTimelineItem<Comment>

export function isPostEventTimelineItem<T>(item: PostTimelineItem<T>): item is PostEventTimelineItem {
    return item.type === 'event'
}

export function isPostCommentTimelineItem<T>(item: PostTimelineItem<T>): item is PostCommentTimelineItem<T> {
    return item.type === 'comment'
}

export type PostTimeline<T extends RichTextComment = RichTextComment> = Array<PostTimelineItem<T>>
export type PlainTextPostTimeline = Array<PostTimelineItem<PlainTextComment>>
export type BackwardsCompatiblePostTimeline = PostTimeline | PlainTextPostTimeline
